/**
 * Retail POS tied to the chart (`BACKLOG.md` M9) — **narrowly scoped on purpose**, per the task brief:
 * a chart-linked product/retail sale record, never a general inventory/POS system. There is no stock
 * table, no reorder point, no vendor/purchase-order model anywhere in this module — a practice's real
 * inventory system (if it has one) is out of scope, and this module only records that a retail sale
 * happened against a specific patient's chart, which is the part the research (Pabau, Aesthetic
 * Record) found genuinely load-bearing for a med-spa: retail revenue tied to the patient relationship,
 * not shelf-stock accounting.
 */

export const RETAIL_PAYMENT_METHODS = ['card', 'cash', 'account_credit'] as const;
export type RetailPaymentMethod = (typeof RETAIL_PAYMENT_METHODS)[number];

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isRetailPaymentMethod(value: string): value is RetailPaymentMethod {
  return includes(RETAIL_PAYMENT_METHODS, value);
}

/**
 * `quantity * unitPriceCents`, in integer cents. The total is never accepted from the client at
 * all — `aestheticsRequests.ts`'s `createRetailSaleRequestSchema` has no `totalPriceCents` field,
 * only `quantity` and `unitPriceCents`, and `server/src/aesthetics/retailSaleService.ts` calls this
 * function to derive the stored total server-side. A client-supplied total would be a second,
 * independently-editable copy of a value that is *entirely* determined by two other fields already
 * on the same request — exactly the kind of redundant, driftable input
 * `claude-kit/rules/invariants.md`'s "server-authoritative values never come from the request"
 * exists to rule out, even though `quantity`/`unitPriceCents` themselves are legitimately
 * staff-entered rather than system-owned.
 */
export function computeRetailSaleTotalCents(quantity: number, unitPriceCents: number): number {
  return quantity * unitPriceCents;
}
